import React, { useEffect, useState } from "react";
import axiosInstance from "src/api/axios";
import { toast } from "react-toastify";
import classNames from "classnames";
import Chats from "src/components/Chats";
import * as Tooltip from "@radix-ui/react-tooltip";
import { ACTIVE_LOGIN_ROUTES } from "src/utils/loginRoutes";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface InstagramBusinessUser {
  id: string;
  username: string;
  name?: string;
  email?: string;
  profilePicture?: string;
  [key: string]: any;
}

const InstagramBusinessDashboard: React.FC = () => {
  const [user, setUser] = useState<InstagramBusinessUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [media, setMedia] = useState<any[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [mentions, setMentions] = useState<any[]>([]);
  const [mentionsLoading, setMentionsLoading] = useState(false);
  const [mentionsError, setMentionsError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"media" | "mentions" | "chat">(
    "mentions"
  );
  const [insights, setInsights] = useState<any[]>([]);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      setError(null);
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await axiosInstance.get(
          `${backendUrl}/instagram-business/user`,
          accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined
        );
        const userData =
          response.data && response.data.data && response.data.data[0];
        if (userData) {
          setUser(userData);
        } else {
          setUser(null);
          setError("No user data found.");
        }
      } catch (err: any) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch user data"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const fetchMedia = async () => {
      setMediaLoading(true);
      setMediaError(null);
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await axiosInstance.get(
          `${backendUrl}/instagram-business/media`,
          accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined
        );
        setMedia(response.data?.data || []);
      } catch (err: any) {
        setMediaError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch media"
        );
      } finally {
        setMediaLoading(false);
      }
    };
    fetchMedia();
  }, []);

  useEffect(() => {
    const fetchMentions = async () => {
      setMentionsLoading(true);
      setMentionsError(null);
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await axiosInstance.get(
          `${backendUrl}/instagram-business/mentions`,
          accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined
        );
        setMentions(response.data?.data || []);
      } catch (err: any) {
        setMentionsError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch mentions"
        );
      } finally {
        setMentionsLoading(false);
      }
    };
    fetchMentions();
  }, []);

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await axiosInstance.get(
          `${backendUrl}/instagram-business/insights`,
          accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined
        );
        console.log("insights", response.data?.data || []);
        setInsights(response.data?.data || []);
      } catch (err: any) {
        console.log(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to fetch mentions"
        );
      }
    };
    fetchInsights();
  }, []);

  return (
    <div
      className={classNames(
        "flex flex-col items-center min-h-screen bg-gray-50",
        {
          "justify-center": !user,
        }
      )}
    >
      <h1 className="sr-only">Instagram Business Dashboard</h1>
      {loading ? (
        <p className="text-lg text-gray-700">Loading user data...</p>
      ) : error ? (
        <div className="text-lg text-red-500 flex flex-col items-center">
          {/* <p>{error}</p> */}
          <p className="mt-4 text-gray-700">
            Connect your Instagram account to use this dashboard.{" "}
            <a
              href={ACTIVE_LOGIN_ROUTES.connect}
              className="text-blue-600 underline hover:text-blue-800"
            >
              Click here
            </a>
            .
          </p>
        </div>
      ) : user ? (
        <>
          <div className="bg-white shadow-md rounded-xl p-8 w-full max-w-xl flex flex-col">
            <div className="flex items-center mb-4">
              {user.profile_picture_url && (
                <img
                  src={user.profile_picture_url}
                  alt="Profile"
                  className="w-20 h-20 rounded-full object-cover bg-gray-200 mr-6"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-2xl font-bold text-gray-900 leading-tight">
                      {user.name}
                    </div>
                    <a
                      href={`https://instagram.com/${user.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-lg text-gray-500 hover:underline block"
                    >
                      @{user.username}
                    </a>
                  </div>
                  <div className="flex gap-8 mt-4 sm:mt-0 sm:ml-8">
                    <div className="text-center">
                      <div className="font-bold text-gray-900 text-lg">
                        Followers
                      </div>
                      <div className="text-gray-800 text-lg">
                        {user.followers_count}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-gray-900 text-lg">
                        Following
                      </div>
                      <div className="text-gray-800 text-lg">
                        {user.follows_count}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-gray-900 text-lg">
                        <Tooltip.Provider>
                          <Tooltip.Root>
                            <Tooltip.Trigger asChild>
                              <span>Reach</span>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                              <Tooltip.Content
                                sideOffset={5}
                                className="bg-black text-white px-2 py-1 rounded text-xs"
                              >
                                Reach (Last 7 days)
                              </Tooltip.Content>
                            </Tooltip.Portal>
                          </Tooltip.Root>
                        </Tooltip.Provider>
                      </div>
                      {/* <div className="text-gray-800 text-lg">
                        {!insights?.at(0)?.values?.at(0)?.value &&
                        !insights?.at(0)?.values?.at(1)?.value ? (
                          <>
                            {insights?.at(0)?.values?.at(0)?.value ?? 0}-
                            {insights?.at(0)?.values?.at(1)?.value ?? 0}
                          </>
                        ) : (
                          0
                        )}
                      </div> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {user.biography && (
              <div className="mt-2 text-gray-700 text-lg whitespace-pre-line">
                {user.biography}
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="mt-10 w-full max-w-4xl">
            <div className="flex border-b mb-4">
              {/* <button
                className={classNames(
                  "px-4 py-2 font-semibold",
                  activeTab === "media"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-600 hover:text-blue-600"
                )}
                onClick={() => setActiveTab("media")}
              >
                Media
              </button> */}
              <button
                className={classNames(
                  "px-4 py-2 font-semibold",
                  activeTab === "mentions"
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-600 hover:text-blue-600"
                )}
                onClick={() => setActiveTab("mentions")}
              >
                Mentions
              </button>
              <button
                className={classNames(
                  'px-4 py-2 font-semibold',
                  activeTab === 'chat'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-600 hover:text-blue-600'
                )}
                onClick={() => setActiveTab('chat')}
              >
                Chat
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === "media" && (
              <div>
                <h2 className="text-xl font-bold mb-2">Media</h2>
                {mediaLoading ? (
                  <p>Loading media...</p>
                ) : mediaError ? (
                  <p className="text-red-500">{mediaError}</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg shadow">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr>
                          <th className="px-4 py-2 text-left">Preview</th>
                          <th className="px-4 py-2 text-left">Type</th>
                          <th className="px-4 py-2 text-left">Caption</th>
                          <th className="px-4 py-2 text-left">Timestamp</th>
                          <th className="px-4 py-2 text-left">Link</th>
                        </tr>
                      </thead>
                      <tbody>
                        {media.map((item) => (
                          <tr key={item.id} className="border-t">
                            <td className="px-4 py-2">
                              {item.media_type === "IMAGE" ||
                              item.media_type === "CAROUSEL_ALBUM" ? (
                                <img
                                  src={item.media_url}
                                  alt="media"
                                  className="w-16 h-16 object-cover rounded"
                                />
                              ) : item.media_type === "VIDEO" ? (
                                <video
                                  src={item.media_url}
                                  controls
                                  className="w-16 h-16 object-cover rounded"
                                />
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                            <td className="px-4 py-2">{item.media_type}</td>
                            <td
                              className="px-4 py-2 max-w-xs truncate"
                              title={item.caption}
                            >
                              {item.caption || "-"}
                            </td>
                            <td className="px-4 py-2">
                              {item.timestamp
                                ? new Date(item.timestamp).toLocaleString()
                                : "-"}
                            </td>
                            <td className="px-4 py-2">
                              <a
                                href={item.permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                View
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {media.length === 0 && (
                      <div className="p-4 text-gray-500">No media found.</div>
                    )}
                  </div>
                )}
              </div>
            )}
            {activeTab === "mentions" && (
              <div>
                <h2 className="text-xl font-bold mb-2">Mentions</h2>
                {mentionsLoading ? (
                  <p>Loading mentions...</p>
                ) : mentionsError ? (
                  <p className="text-red-500">{mentionsError}</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg shadow">
                    <table className="min-w-full bg-white">
                      <thead>
                        <tr>
                          <th className="px-4 py-2 text-left">Preview</th>
                          <th className="px-4 py-2 text-left">Type</th>
                          <th className="px-4 py-2 text-left">Caption</th>
                          <th className="px-4 py-2 text-left">Timestamp</th>
                          <th className="px-4 py-2 text-left">Link</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mentions.map((item) => (
                          <tr key={item.id} className="border-t">
                            <td className="px-4 py-2">
                              {item.media_type === "IMAGE" ||
                              item.media_type === "CAROUSEL_ALBUM" ? (
                                <img
                                  src={item.media_url}
                                  alt="media"
                                  className="w-16 h-16 object-cover rounded"
                                />
                              ) : item.media_type === "VIDEO" ? (
                                <video
                                  src={item.media_url}
                                  controls
                                  className="w-16 h-16 object-cover rounded"
                                />
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                            <td className="px-4 py-2">{item.media_type}</td>
                            <td
                              className="px-4 py-2 max-w-xs truncate"
                              title={item.caption}
                            >
                              {item.caption || "-"}
                            </td>
                            <td className="px-4 py-2">
                              {item.timestamp
                                ? new Date(item.timestamp).toLocaleString()
                                : "-"}
                            </td>
                            <td className="px-4 py-2">
                              <a
                                href={item.permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                View
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {mentions.length === 0 && (
                      <div className="p-4 text-gray-500">
                        No mentions found.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'chat' && (
              <div>
                <h2 className="text-xl font-bold mb-2">Chat / Messaging</h2>
                <Chats />
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-lg text-gray-700 flex flex-col items-center">
          <p>No user data available.</p>
          <p className="mt-4 text-gray-700">
            Connect your Instagram account to use this dashboard.{" "}
            <a
              href={ACTIVE_LOGIN_ROUTES.connect}
              className="text-blue-600 underline hover:text-blue-800"
            >
              Click here
            </a>
            .
          </p>
        </div>
      )}
    </div>
  );
};

export default InstagramBusinessDashboard;
